import { useState } from 'react';
import { AlertTriangle, Boxes, Clock, Snowflake, Warehouse } from 'lucide-react';

import ChartCard from '@/features/reports/components/ChartCard';
import RankBarChart from '@/features/reports/components/charts/RankBarChart';
import { SimpleKpi } from '@/features/reports/components/KpiTile';
import ReportTable from '@/features/reports/components/ReportTable';
import { useReportOutlet } from '@/features/reports/hooks/useReportOutlet';
import { useInventoryReport } from '@/features/reports/hooks/useReports';
import {
  formatDecimal,
  formatNumber,
  formatRangeCaption,
  formatToman,
} from '@/features/reports/lib/format';
import { persianDisplayText } from '@/lib/persianUi';

const productDetailsLabel = (row) =>
  [row.brand, ...(row.categories ?? [])]
    .map((value) => persianDisplayText(value, ''))
    .filter(Boolean)
    .join(' • ') || '—';

const LIMIT_OPTIONS = [50, 100, 200, 500];

/** Stock levels, stock value, velocity and the products that need attention. */
export default function InventoryReport() {
  const { query, filters } = useReportOutlet();
  const [onlyAttention, setOnlyAttention] = useState(false);
  const [limit, setLimit] = useState(100);
  const [threshold, setThreshold] = useState(10);

  const inventory = useInventoryReport(query, {
    limit,
    onlyAttention,
    lowStockThreshold: threshold,
  });

  const data = inventory.data;
  const rows = data?.rows ?? [];

  const chartRows = [...rows]
    .sort((a, b) => Number(b.stock_value ?? 0) - Number(a.stock_value ?? 0))
    .slice(0, 12)
    .map((row) => ({
      ...row,
      label: persianDisplayText(row.name_fa, 'نام ثبت‌نشده'),
    }));

  return (
    <div className="reports-page">
      <div className="report-section-head">
        <h2 className="section-title">موجودی و انبار</h2>
        <p className="report-section-caption">
          {formatRangeCaption(filters.from, filters.to)} • سرعت فروش بر پایه همین بازه محاسبه می‌شود
        </p>
      </div>

      <div className="kpi-grid report-kpi-grid">
        <SimpleKpi
          title="ارزش موجودی"
          value={data?.total_stock_value}
          format={formatToman}
          icon={Warehouse}
          accent="green"
        />
        <SimpleKpi
          title="تعداد کالای موجود"
          value={data?.total_stock_units}
          format={formatNumber}
          icon={Boxes}
          accent="blue"
          secondary={`${formatNumber(data?.total_products ?? 0)} کالا`}
        />
        <SimpleKpi
          title="ناموجود"
          value={data?.out_of_stock_count}
          format={formatNumber}
          icon={AlertTriangle}
          accent="red"
        />
        <SimpleKpi
          title="کم‌موجود"
          value={data?.low_stock_count}
          format={formatNumber}
          icon={AlertTriangle}
          accent="amber"
          hint={`آستانه: ${formatNumber(data?.low_stock_threshold ?? threshold)} عدد`}
        />
        <SimpleKpi
          title="کالاهای فاسدشدنی"
          value={data?.perishable_count}
          format={formatNumber}
          icon={Snowflake}
          accent="purple"
        />
        <SimpleKpi
          title="کالاهای کم‌گردش"
          value={data?.slow_moving_count}
          format={formatNumber}
          icon={Clock}
          accent="gray"
          hint="بدون فروش در بازه انتخابی"
        />
      </div>

      <ChartCard
        title="بیشترین ارزش موجودی"
        subtitle="۱۲ کالای نخست بر پایه ارزش موجودی"
        actions={
          <div className="report-card-actions-group">
            <label className="report-inline-field">
              <span>آستانه کم‌موجودی</span>
              <input
                className="report-date-input report-number-input"
                type="number"
                min="0"
                value={threshold}
                onChange={(event) => setThreshold(Math.max(0, Number(event.target.value) || 0))}
              />
            </label>
            <label className="report-inline-field">
              <span>تعداد</span>
              <select
                className="report-select"
                value={limit}
                onChange={(event) => setLimit(Number(event.target.value))}
                aria-label="تعداد کالا"
              >
                {LIMIT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {formatNumber(option)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className={`report-chip${onlyAttention ? ' active' : ''}`}
              onClick={() => setOnlyAttention((value) => !value)}
            >
              فقط کالاهای نیازمند توجه
            </button>
          </div>
        }
        height={420}
        isLoading={inventory.isLoading}
        isError={inventory.isError}
        error={inventory.error}
        isEmpty={!chartRows.length}
      >
        <RankBarChart
          rows={chartRows}
          valueKey="stock_value"
          money
          height={420}
          yAxisWidth={170}
          showShare={false}
        />
      </ChartCard>

      <ChartCard
        title="جدول موجودی"
        subtitle="سرعت فروش روزانه و روزهای پوشش موجودی"
        bodyClassName="report-card-body-table"
        isLoading={inventory.isLoading}
        isError={inventory.isError}
        error={inventory.error}
        isEmpty={!rows.length}
      >
        <ReportTable
          rows={rows}
          rowKey={(row) => row.product_id}
          maxHeight={620}
          columns={[
            {
              key: 'name',
              header: 'کالا',
              render: (row) => (
                <div className="report-cell-stack">
                  <span className="report-cell-primary">{persianDisplayText(row.name_fa, 'نام ثبت‌نشده')}</span>
                  <span className="report-cell-secondary">{productDetailsLabel(row)}</span>
                </div>
              ),
            },
            {
              key: 'stock_quantity',
              header: 'موجودی',
              render: (row) => (
                <span
                  className={
                    row.stock_quantity <= 0
                      ? 'report-cell-danger'
                      : row.stock_quantity <= (data?.low_stock_threshold ?? threshold)
                        ? 'report-cell-warn'
                        : undefined
                  }
                >
                  {formatNumber(row.stock_quantity)}
                </span>
              ),
            },
            { key: 'stock_value', header: 'ارزش موجودی', render: (row) => formatToman(row.stock_value) },
            { key: 'units_sold', header: 'فروش در بازه', render: (row) => formatNumber(row.units_sold) },
            {
              key: 'daily_velocity',
              header: 'سرعت روزانه',
              render: (row) => formatDecimal(row.daily_velocity, 2),
            },
            {
              key: 'days_of_cover',
              header: 'روزهای پوشش',
              render: (row) =>
                row.days_of_cover === null || row.days_of_cover === undefined ? (
                  <span className="report-cell-secondary">—</span>
                ) : (
                  <span className={row.days_of_cover <= 7 ? 'report-cell-warn' : undefined}>
                    {formatDecimal(row.days_of_cover, 1)}
                  </span>
                ),
            },
            {
              key: 'flags',
              header: 'ویژگی',
              render: (row) => (
                <span className="report-badges">
                  {row.is_perishable && <span className="badge badge-amber">فاسدشدنی</span>}
                  {row.is_kalabarg_eligible && <span className="badge badge-green">کالابرگ</span>}
                </span>
              ),
            },
          ]}
        />
      </ChartCard>
    </div>
  );
}
